<template>
  <div class="settings-section">
    <h4 class="text-h4 q-mt-none q-mb-lg">{{ $t('events.eventSettings') }}</h4>

    <div v-if="loading" class="events-loading q-py-xl flex flex-center column">
      <q-spinner color="primary" size="3em" />
      <p class="q-mt-md">{{ $t('events.loading') }}</p>
    </div>

    <div
      v-else-if="totalEvents === 0"
      class="events-empty q-pa-xl flex flex-center column"
    >
      <q-icon name="event_busy" color="grey-5" size="4rem" />
      <p class="text-subtitle1 text-grey-7 q-mt-md">
        {{ $t('events.noEventsMessage') }}
      </p>
    </div>

    <q-list v-else class="events-list">
      <div class="description-card q-pa-md q-mb-lg">
        <div class="row items-center">
          <div class="col-auto q-mr-md">
            <q-icon name="checklist" color="info" size="24px" />
          </div>
          <div class="col">{{ $t('userPanel.settings.description') }}</div>
        </div>
      </div>

      <div
        v-for="event in events"
        :key="event.id"
        class="event-item"
        :class="{ 'is-editing': event.editing }"
      >
        <div class="event-details">
          <div class="event-title">{{ event.post_title }}</div>
          <div class="event-location">
            <q-icon name="location_on" size="xs" />
            {{ getCityName(event.assign_city) }}
            <q-separator vertical inset color="grey-6" class="q-mx-sm" />
            <q-icon name="event" size="xs" />
            {{ formatDate(event.gear_date) }}
          </div>
        </div>

        <div class="event-actions">
          <q-btn
            v-if="!event.editing"
            color="secondary"
            :label="$t('common.edit')"
            @click="$emit('toggle-edit', event.id)"
            dense
            icon="edit"
            class="q-px-md"
          />
          <div v-else class="sale-settings">
            <q-checkbox
              v-model="event.disableBulkSale"
              :label="$t('userPanel.settings.disableBulkSale')"
              color="primary"
              class="text-weight-medium"
            />
            <q-input
              v-model.number="event.photoPrice"
              :label="$t('userPanel.settings.singlePhotoPrice')"
              type="number"
              min="4"
              step="0.1"
              :rules="[requiredRule, minPriceRule(4)]"
              hide-bottom-space
              filled
              dense
              class="number-input"
              :disable="!event.disableBulkSale || event.saving"
            />
            <div class="flex">
              <q-btn
                color="primary"
                :label="$t('common.save')"
                @click="saveEvent(event)"
                :loading="event.saving"
                unelevated
                rounded
                dense
                class="q-px-md"
              />
              <q-btn
                icon="close"
                flat
                round
                size="sm"
                @click="$emit('toggle-edit', event.id)"
              />
            </div>
          </div>
        </div>
      </div>
    </q-list>

    <div class="pagination-controls q-mt-md" v-if="totalPages > 1">
      <q-btn
        round
        flat
        icon="chevron_left"
        :disable="currentPage === 1 || loading"
        @click="changePage(currentPage - 1)"
      />
      <span>{{ currentPage }} / {{ totalPages }}</span>
      <q-btn
        round
        flat
        icon="chevron_right"
        :disable="currentPage === totalPages || loading"
        @click="changePage(currentPage + 1)"
      />
    </div>
  </div>
</template>

<script>
import { date, Notify } from 'quasar';

export default {
  name: 'SettingsCardEvents',
  props: {
    events: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    citiesList: { type: Array, default: () => [] },
    requiredRule: { type: Function, required: true },
    minPriceRule: { type: Function, required: true },
    fetchGears: Function,
    perPage: { type: Number, default: 6 },
    totalEvents: { type: Number, default: 0 },
  },
  data() {
    return {
      currentPage: 1,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalEvents / this.perPage);
    },
  },
  methods: {
    async changePage(page) {
      if (page < 1 || page > this.totalPages) {
        return;
      }

      this.currentPage = page;
      if (this.fetchGears) {
        await this.fetchGears({
          page: this.currentPage,
          perPage: this.perPage,
        });
      }
    },
    getCityName(cityId) {
      const city = this.citiesList.find(
        (city) => city.id.toString() === cityId.toString()
      );

      return city ? city.label : cityId;
    },
    formatDate(dateString) {
      dateString = String(dateString);
      if (/^\d{8}$/.test(dateString)) {
        dateString = `${dateString.slice(0, 4)}/${dateString.slice(
          4,
          6
        )}/${dateString.slice(6, 8)}`;
      }

      return date.formatDate(dateString, 'YYYY/MM/DD');
    },
    saveEvent(event) {
      if (event.disableBulkSale) {
        const rules = [this.requiredRule, this.minPriceRule(4)];
        const value = event.photoPrice;
        const isValid = rules.every((rule) => rule(value) === true);
        if (!isValid) {
          Notify.create({
            message: this.$t('validation.minPrice', { min: 4 }),
            type: 'negative',
            position: 'bottom',
            timeout: 3000,
          });

          return;
        }
      }
      this.$emit('save-event', {
        eventId: event.id,
        disableBulkSale: event.disableBulkSale,
        photoPrice: event.photoPrice,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.settings-section {
  @extend .settings-section;
}

.description-card {
  @extend .description-card;
  border-left: 4px solid $info;
}

.events-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.event-item {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0.5rem;
  border-radius: 6px;
  background-color: #f4f4f4;
  gap: 1rem;
  border: 1px solid #e9e9e9;

  &.is-editing {
    background-color: #e9e9e9;
  }
}

.event-details {
  align-self: stretch;
  flex: 1;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e9e9e9;
}

.event-title {
  font-weight: 600;
  margin-bottom: 0.25rem;
  font-size: 1rem;
}

.event-location {
  font-size: 0.875rem;
  color: #666;
}

.event-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.sale-settings {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
}

.number-input {
  width: 100%;
}

.events-empty,
.events-loading {
  border-radius: 8px;
  background-color: #f8f9fa;
  text-align: center;
  min-height: 200px;
}

.pagination-controls {
  text-align: center;
  padding: 0.75rem;
}

@media screen and (min-width: $breakpoint-sm-min) {
  .event-item {
    flex-direction: row;
  }

  .event-details {
    border-bottom: none;
    border-right: 1px solid #e9e9e9;
    padding-right: 0.5rem;
  }

  .sale-settings {
    flex-direction: row;

    .number-input {
      width: 120px;
    }
  }
}
</style>
